// M005-F0070 (M005-D005 §4.1 tab set + §7.5 amendment, owner ruling
// 2026-09-13; AMENDED 2026-09-16 per WebUI #108 / M004-F0637 §2) — the
// Bookings Hub "Price plans" tab: the org's plans in one table; clicking a
// row opens the plan's EDIT DRAWER (side drawer, UI-4) with editable name +
// rates; "Add price plan"; per-plan "Delete" (in-use plans protected by
// Core's 409 PRICING_PLAN_IN_USE). The old preview/confirm MODAL is
// retired: a rate change is not destructive, so the R4 impact preview now
// renders INLINE in the drawer — it is fetched automatically while the form
// differs on rates, and Save stays BLOCKED until a readable impact is shown
// (a rate edit whose impact preview cannot be read is NEVER committed — a
// silent bulk reprice is the one failure this surface must not have). ONE
// Save click commits; no second confirmation dialog. The StepUpDialog is
// the only permitted second dialog and REPLAYS the interrupted save/delete.
//
// Permission model (R1): the tab itself is gated in BookingsHub.js's
// tabsForPermissions on `pricing_plans_view === true` (fail closed). Inside
// the tab, every mutating control additionally requires
// `pricing_plans_manage === true` — rendered DISABLED with a tooltip
// otherwise, never hidden: a viewer must see that management exists and is
// not theirs (owner ruling wording: "hide/disable with tooltip otherwise").
//
// Money crosses the wire exactly like the retired RoomPricingPanel idiom
// (verified against Core pricing.routes.ts on origin/main 2026-09-13):
//   - GET /plans rows carry raw `first_hour_rate_minor` /
//     `long_hour_rate_minor` integer columns (or null) — only this divide-
//     by-100 boundary renders pounds.
//   - POST/PATCH bodies take `first_hour_rate` / `long_hour_rate` as DECIMAL
//     STRINGS ("12.50") — never minor units, never a JSON number — and
//     `long_rate_threshold_minutes` as a positive integer.
//   - createPlanSchema needs `effective_from` (YYYY-MM-DD) and — per its own
//     .refine — a scope: `room_id` for a room plan or `is_template: true`
//     for an org template. chk_plan_rates_present (Core pricing.service.ts)
//     is STRONGER than "at least one rate": a plan without a template parent
//     must state EVERY rate (first + long + threshold) or Core answers 400
//     PLAN_RATES_REQUIRED — this form validates the same rule client-side.
//   - updatePlanSchema is .strict() and now (M005-F0050 / #1386) accepts
//     `grandfather: true` — default OFF when omitted. The preview query flag
//     and the commit flag are the SAME choice, shown once, here.
//
// With the Room-edit Pricing tab retired (M004-F0617 amendment, executed by
// M004-F0679 / WebUI #122 option 2), this drawer is the ONLY plan-management
// surface, so it carries the two actions only that tab offered:
//   - "Set as default": Core's atomic designate-default (M005-D004 §10.9
//     E1/E2) — a per-plan ACTION, never a checkbox, sent as exactly
//     PATCH {is_public_default:true}; it MOVES the default, there is no unset;
//     when another active plan holds the default the drawer first says, inline,
//     which plan it replaces. (The design !449 reached, T-M004-0027 U1-U4.)
//   - "Retire": part of the ONE Save — status:'retired' shows the same inline
//     reprice impact as a rate edit, and Save stays blocked until it is readable.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pricingApi, roomApi } from '../../services/api';
import PlanZonesSection from './PlanZonesSection';
import { isPlanZonesBackendAvailable } from '../../utils/priceFeatureFlags';
import { normalisePlan } from '../../utils/priceWire';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import StepUpDialog from '../auth/StepUpDialog';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';
import './BookingDrawer.css';

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

const toBool = (value) => value === true || value === 1;

/** B4-equivalent, copied idiom from RoomPricingPanel: display only. */
const formatMinorPerHour = (minor) => {
  if (minor === null || minor === undefined || minor === '') return null;
  const numeric = Number(minor);
  if (!Number.isFinite(numeric)) return null;
  return `£${(numeric / 100).toFixed(2)}/hr`;
};

const poundsFromMinor = (minor) => {
  const formatted = formatMinorPerHour(minor);
  return formatted ? formatted.replace('/hr', '') : '';
};

const planRatesText = (plan) => {
  const parts = [];
  const first = formatMinorPerHour(plan.first_hour_rate_minor);
  const long = formatMinorPerHour(plan.long_hour_rate_minor);
  if (first) parts.push(`First hour ${first}`);
  if (long) parts.push(`Long hour ${long}`);
  if (plan.template_plan_id !== null && plan.template_plan_id !== undefined && parts.length === 0) {
    parts.push('Inherits from template');
  }
  return parts.length > 0 ? parts.join(' · ') : 'No rates set';
};

const planScopeText = (plan, roomNameFor) => {
  if (toBool(plan.is_template)) return 'Org template';
  if (plan.room_id === null || plan.room_id === undefined) return 'Unscoped';
  return roomNameFor(plan.room_id) || `Room ${plan.room_id}`;
};

/**
 * The PATCH payload for an editor save — updatePlanSchema field-for-field,
 * changed fields only. Rate fields the user left blank mean "keep the
 * current rate" (the RoomPricingPanel idiom) and are omitted. `grandfather`
 * is NOT part of the patch — the caller adds it on the commit so the preview
 * and the PATCH always carry the same choice.
 */
export const buildPlanPatch = (plan, form) => {
  const patch = {};
  const name = (form.name || '').trim();
  if (name && name !== plan.name) patch.name = name;
  if (form.firstHourRate !== '') patch.first_hour_rate = form.firstHourRate;
  if (form.longHourRate !== '') patch.long_hour_rate = form.longHourRate;
  const threshold = `${form.thresholdMinutes}`.trim();
  if (threshold !== '' && Number(threshold) !== plan.long_rate_threshold_minutes) {
    patch.long_rate_threshold_minutes = Number(threshold);
  }
  return patch;
};

/** The rate-affecting keys whose edit must show the impact preview first. */
const RATE_KEYS = ['first_hour_rate', 'long_hour_rate', 'long_rate_threshold_minutes'];

export const patchTouchesRates = (patch) => RATE_KEYS.some((key) => patch[key] !== undefined);

/**
 * A patch needs the inline impact BEFORE it can commit when it touches a rate
 * OR retires the plan (a retired plan stops being offered, and its bookings'
 * reprice behaviour is exactly what the preview classifies).
 */
export const patchNeedsImpact = (patch) => patchTouchesRates(patch) || patch.status === 'retired';

/**
 * M004-F0679: the active room default of a room, from the loaded plan rows
 * (Core's single-default rule, §10.9 E1: at most one active, undeleted plan
 * per room). A template is never a room default.
 */
export const activeDefaultOf = (plans, roomId) => (
  plans.find((plan) => (
    !toBool(plan.is_template)
    && String(plan.room_id) === String(roomId)
    && toBool(plan.is_public_default)
    && (plan.status || 'active') === 'active'
  )) || null
);

/** A plan can be designated the room default only if it is an active, non-template room plan that is not one already. */
export const canDesignateDefault = (plan) => Boolean(plan)
  && !toBool(plan.is_template)
  && plan.room_id !== null && plan.room_id !== undefined
  && (plan.status || 'active') === 'active'
  && !toBool(plan.is_public_default);

/**
 * The POST payload for "Add price plan" — createPlanSchema field-for-field.
 * A plan needs a scope (org template or a room) and, per
 * chk_plan_rates_present, EVERY rate unless it inherits from a template.
 * Returns `{ payload }` or `{ error }` without ever talking to the network.
 */
export const buildAddPayload = (form) => {
  const name = (form.name || '').trim();
  if (!name) return { error: 'A name is required.' };
  const templateId = form.templatePlanId ? Number(form.templatePlanId) : null;
  const first = (form.firstHourRate || '').trim();
  const long = (form.longHourRate || '').trim();
  const threshold = (form.thresholdMinutes || '').trim();
  const money = /^\d{1,9}(\.\d{1,2})?$/;
  if (!templateId) {
    // chk_plan_rates_present, client-side: a standalone plan must state
    // EVERY rate — Core answers 400 PLAN_RATES_REQUIRED otherwise (first
    // surfaced on main pipeline 9136, job e2e:playwright 224928).
    if (!first || !money.test(first)) return { error: 'First hour rate is required as a decimal amount, e.g. 12.50.' };
    if (!long || !money.test(long)) return { error: 'Long hour rate is required as a decimal amount, e.g. 10.00.' };
    if (!threshold || !Number.isInteger(Number(threshold)) || Number(threshold) < 1) {
      return { error: 'Long rate threshold (minutes) is required as a positive whole number.' };
    }
  } else {
    if (first && !money.test(first)) return { error: 'First hour rate must be a decimal amount, e.g. 12.50.' };
    if (long && !money.test(long)) return { error: 'Long hour rate must be a decimal amount, e.g. 10.00.' };
    if (threshold && (!Number.isInteger(Number(threshold)) || Number(threshold) < 1)) {
      return { error: 'Long rate threshold (minutes) must be a positive whole number.' };
    }
  }
  if (form.scope === 'room' && !form.roomId) return { error: 'Choose a room for this plan.' };
  if (!form.effectiveFrom) return { error: 'Effective from is required.' };

  const payload = {
    name,
    effective_from: form.effectiveFrom,
  };
  if (form.scope === 'room') payload.room_id = Number(form.roomId);
  else payload.is_template = true;
  if (templateId) payload.template_plan_id = templateId;
  if (first) payload.first_hour_rate = first;
  if (long) payload.long_hour_rate = long;
  if (threshold) payload.long_rate_threshold_minutes = Number(threshold);
  payload.currency = (form.currency || 'GBP').toUpperCase();
  return { payload };
};

const emptyEditorForm = (plan) => ({
  name: plan?.name || '',
  firstHourRate: '',
  longHourRate: '',
  thresholdMinutes: '',
});

const emptyAddForm = () => ({
  name: '',
  scope: 'template',
  roomId: '',
  templatePlanId: '',
  firstHourRate: '',
  longHourRate: '',
  thresholdMinutes: '',
  effectiveFrom: new Date().toISOString().slice(0, 10),
  currency: 'GBP',
});

/** Inline error text for a failed mutation — 401/401-style bars first. */
const mutationErrorText = (error, fallback) => {
  const status = error?.response?.status;
  if (status === 401) return 'Your session has expired — sign in again to manage price plans.';
  if (status === 403) {
    const denied = error?.response?.data?.error === 'ORG_PERMISSION_DENIED';
    return denied
      ? 'You do not have the pricing_plans_manage permission for this organisation.'
      : `You do not have permission to do that. ${error?.response?.data?.message || ''}`.trim();
  }
  return error?.response?.data?.message
    || error?.response?.data?.error?.message
    || error?.message
    || fallback;
};

const MANAGE_TOOLTIP = 'Requires the pricing_plans_manage permission.';

const PricePlansHubTab = ({ selectedOrgId, organisationName, permissions }) => {
  const canView = toBool(permissions?.pricing_plans_view);
  const canManage = toBool(permissions?.pricing_plans_manage);

  const [plans, setPlans] = useState([]);
  const [rooms, setRooms] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [editorForm, setEditorForm] = useState(emptyEditorForm(null));
  const [editorError, setEditorError] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm());
  const [addError, setAddError] = useState('');

  // The R4 preview-before-save, INLINE (M004-F0637 amendment): editing a
  // rate auto-fetches the impact for the CURRENT form (the grandfather
  // choice re-queries it) and Save stays blocked until it is readable.
  // `impactSeqRef` discards stale previews when edits outrun the reads.
  const [impact, setImpact] = useState(null);
  const [impactError, setImpactError] = useState('');
  const [impactLoading, setImpactLoading] = useState(false);
  const [grandfather, setGrandfather] = useState(false);
  const impactSeqRef = useRef(0);
  // M004-F0679: "Retire" is a request that rides the ONE Save (with its impact);
  // "Set as default" may need an inline sentence first when it replaces a plan.
  const [retireRequested, setRetireRequested] = useState(false);
  const [designateConfirm, setDesignateConfirm] = useState(false);
  const [designateBusy, setDesignateBusy] = useState(false);

  // The StepUpDialog is the only permitted second dialog; its completion
  // REPLAYS the interrupted save/delete (the LedgerAccountPage.js idiom).
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const stepUpRetryRef = useRef(null);

  // R3: a 409 PRICING_PLAN_IN_USE refusal, kept verbatim so the named
  // references (Core detail[], one { reason, ref } per blocker) render.
  const [deleteConflict, setDeleteConflict] = useState(null);

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.id) === String(selectedPlanId)) || null,
    [plans, selectedPlanId],
  );

  // The editor form always mirrors the SELECTED plan row as last loaded —
  // selecting a plan seeds it, and a successful save's re-load re-seeds it
  // with the fresh rates (blank rate inputs again, new placeholders).
  //
  // But never over UNSAVED edits: a re-load triggered by something else (the Zones section's save re-reads the
  // plans) hands the drawer a new plan object, and an unconditional re-seed silently dropped the rates/name the user
  // had typed. The form re-seeds when another plan is selected, after THIS form's own save (formCommittedRef), or
  // when it holds no edits against the plan it was seeded from.
  const seededPlanRef = useRef(null);
  const formCommittedRef = useRef(false);
  useEffect(() => {
    const previous = seededPlanRef.current;
    seededPlanRef.current = selectedPlan;
    const samePlan = previous && selectedPlan && String(previous.id) === String(selectedPlan.id);
    const committed = formCommittedRef.current;
    formCommittedRef.current = false;
    if (samePlan && !committed) {
      const seededFrom = emptyEditorForm(previous);
      setEditorForm((form) => (
        form.name === seededFrom.name && form.firstHourRate === '' && form.longHourRate === '' && form.thresholdMinutes === ''
          ? emptyEditorForm(selectedPlan)
          : form
      ));
      return;
    }
    setEditorForm(emptyEditorForm(selectedPlan));
  }, [selectedPlan]);

  const roomNameFor = useCallback(
    (roomId) => rooms[String(roomId)] || null,
    [rooms],
  );

  // M004-F0679 (T-M004-0027 U3): the gap is loud, never quiet — every room
  // that has an active plan but no active default is named above the table.
  const roomsWithoutDefault = useMemo(() => {
    const roomIds = [...new Set(plans
      .filter((plan) => !toBool(plan.is_template) && plan.room_id !== null && plan.room_id !== undefined
        && (plan.status || 'active') === 'active')
      .map((plan) => String(plan.room_id)))];
    return roomIds
      .filter((roomId) => activeDefaultOf(plans, roomId) === null)
      .map((roomId) => roomNameFor(roomId) || `Room ${roomId}`);
  }, [plans, roomNameFor]);

  const load = useCallback(async ({ signal } = {}) => {
    if (!selectedOrgId) {
      setPlans([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const plansResponse = await pricingApi.listPlans(selectedOrgId, { signal });
      const payload = unwrap(plansResponse);
      setPlans(Array.isArray(payload?.plans) ? payload.plans.map(normalisePlan) : []);
      // Best-effort room names for the Scope column: a pricing_plans_view
      // holder is not guaranteed rooms_manage, so a failed/forbidden read
      // degrades to "Room <id>", never to a broken table.
      roomApi.getRooms(selectedOrgId, true, false)
        .then((roomsResponse) => {
          const list = unwrap(roomsResponse);
          const rows = Array.isArray(list) ? list : Array.isArray(list?.rooms) ? list.rooms : [];
          const byId = {};
          rows.forEach((room) => { if (room?.id !== undefined) byId[String(room.id)] = room.name; });
          setRooms(byId);
        })
        .catch(() => setRooms({}));
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
        // #107 review 15370 item 1 (port from !399): requireElevated is
        // purpose-scoped, so on a cold session this READ is the first
        // billing.view call and it 401s STEP_UP_REQUIRED. Capture the
        // challenge and replay the SAME read when the dialog completes —
        // never the old "session expired" bar with no rows and no dialog.
        const challenge = getStepUpChallengeFromError(err);
        if (challenge?.id) {
          stepUpRetryRef.current = { kind: 'load' };
          setStepUpChallenge(challenge);
          return;
        }
        setError(mutationErrorText(err, 'Failed to load price plans.'));
      }
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (!canView) return () => {};
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    setSelectedPlanId('');
    setAddOpen(false);
    setImpact(null);
    setImpactError('');
    setDeleteConflict(null);
    load({ signal: controller?.signal });
    return () => controller?.abort();
  }, [canView, load]);

  const openEditorFor = (planId) => {
    const plan = plans.find((candidate) => String(candidate.id) === String(planId)) || null;
    setSelectedPlanId(plan ? String(plan.id) : '');
    setEditorForm(emptyEditorForm(plan));
    setEditorError('');
    setImpact(null);
    setImpactError('');
    setImpactLoading(false);
    setGrandfather(false);
    setRetireRequested(false);
    setDesignateConfirm(false);
    setNotice('');
    setDeleteConflict(null);
  };

  const closeEditor = () => {
    setSelectedPlanId('');
    setEditorForm(emptyEditorForm(null));
    setImpact(null);
    setImpactError('');
    setImpactLoading(false);
    setGrandfather(false);
    setRetireRequested(false);
    setDesignateConfirm(false);
    setEditorError('');
    setDeleteConflict(null);
  };

  const editorDismissHandlers = useDismissOnOutsideClick(closeEditor);

  const handleEditorChange = (event) => {
    const { name, value } = event.target;
    setEditorForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddChange = (event) => {
    const { name, value } = event.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };

  // R4, inline: the impact preview call for the CURRENT form. The
  // grandfather choice maps onto the preview's query flag — so the numbers
  // shown are always the numbers the Save click will produce.
  const runImpactPreview = useCallback(async (plan, grandfatherChoice) => {
    const sequence = impactSeqRef.current + 1;
    impactSeqRef.current = sequence;
    setImpactLoading(true);
    setImpact(null);
    setImpactError('');
    try {
      const response = await pricingApi.previewPlanEditImpact(
        selectedOrgId,
        plan.id,
        { grandfather_existing_bookings: grandfatherChoice === true },
      );
      const body = unwrap(response);
      if (impactSeqRef.current !== sequence) return;
      if (
        typeof body?.toReprice === 'number'
        && typeof body?.held === 'number'
        && typeof body?.refusedBilled === 'number'
      ) {
        setImpact({ toReprice: body.toReprice, held: body.held, refusedBilled: body.refusedBilled });
      } else {
        setImpactError('Impact preview returned an unreadable response — saving is blocked.');
      }
    } catch (err) {
      if (impactSeqRef.current !== sequence) return;
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        stepUpRetryRef.current = { kind: 'preview', planId: plan.id };
        setStepUpChallenge(challenge);
        setImpactError('Verify your identity to read this reprice impact — saving stays blocked until it is shown.');
      } else {
        setImpactError(mutationErrorText(err, 'Impact preview failed — saving is blocked.'));
      }
    } finally {
      if (impactSeqRef.current === sequence) setImpactLoading(false);
    }
  }, [selectedOrgId]);

  // Auto-preview: whenever the OPEN drawer's form touches rates (or the
  // grandfather choice flips), re-fetch the impact for the CURRENT form —
  // keyed on the serialized patch so every edit re-queries and a stale
  // preview can never outlive its own form state.
  const currentPatch = useMemo(
    () => (selectedPlan
      ? { ...buildPlanPatch(selectedPlan, editorForm), ...(retireRequested ? { status: 'retired' } : {}) }
      : {}),
    [selectedPlan, editorForm, retireRequested],
  );
  const patchKey = JSON.stringify(currentPatch);
  const patchTouchesRatesNow = patchNeedsImpact(currentPatch);

  useEffect(() => {
    if (!selectedPlan || !patchTouchesRatesNow) {
      // A name-only edit needs no impact preview — clear any stale one.
      impactSeqRef.current += 1;
      setImpact(null);
      setImpactError('');
      setImpactLoading(false);
      return;
    }
    runImpactPreview(selectedPlan, grandfather);
    // (patchKey encodes the whole patch, so every edit re-queries; the
    // optional-chaining dep is deliberate — the plan ROW identity is what
    // matters, not the object reference.)
  }, [selectedPlan?.id, patchKey, grandfather, runImpactPreview]);

  const handleSaveClick = async () => {
    setEditorError('');
    setNotice('');
    if (!selectedPlan) return;
    const patch = currentPatch;
    if (Object.keys(patch).length === 0) {
      setEditorError('Nothing to save — change a field first.');
      return;
    }
    const touchesRates = patchNeedsImpact(patch);
    if (touchesRates && (impactLoading || !impact || impactError)) {
      // R4: a rate edit NEVER commits without its readable impact — the
      // block is inline, never a second dialog.
      setEditorError(impactError || 'The reprice impact is not readable yet — saving is blocked.');
      return;
    }
    await commitPlanEdit(patch, touchesRates ? grandfather : false);
  };

  const commitPlanEdit = async (patch, withGrandfather) => {
    setEditorError('');
    try {
      const body = withGrandfather === true ? { ...patch, grandfather: true } : patch;
      await pricingApi.updatePlan(selectedOrgId, selectedPlan.id, body);
      setImpact(null);
      setGrandfather(false);
      setRetireRequested(false);
      setNotice('Plan saved.');
      // The editor form re-syncs to the reloaded plan via the selectedPlan
      // effect — no manual reset here, or the stale name would linger. The
      // flag tells that effect this reload IS the form's own save (re-seed),
      // not someone else's (keep unsaved edits).
      formCommittedRef.current = true;
      await load();
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        // Keep the form exactly as it is; the dialog completion replays the
        // SAME PATCH body (the drawer's only permitted second dialog).
        stepUpRetryRef.current = { kind: 'save', patch, withGrandfather };
        setStepUpChallenge(challenge);
        return;
      }
      setEditorError(mutationErrorText(err, 'Failed to save the plan.'));
      setImpact(null);
    }
  };

  const completeStepUp = () => {
    setStepUpChallenge(null);
    const retry = stepUpRetryRef.current;
    stepUpRetryRef.current = null;
    if (retry?.kind === 'load') load();
    else if (retry?.kind === 'save') commitPlanEdit(retry.patch, retry.withGrandfather);
    else if (retry?.kind === 'add') handleAddSubmit();
    else if (retry?.kind === 'delete' && selectedPlan) handleDelete();
    else if (retry?.kind === 'designate' && selectedPlan) runDesignate(selectedPlan);
    else if (retry?.kind === 'preview' && selectedPlan) runImpactPreview(selectedPlan, grandfather);
  };

  // M004-F0679 (T-M004-0027 U1/U2): Set as default. When a DIFFERENT active plan
  // holds the room's default the click first shows the inline sentence naming
  // it (never a modal: nothing is destructive, no booking is repriced); with no
  // current default it calls straight through. The write is exactly
  // PATCH {is_public_default:true} and the tab RE-READS — no optimistic flip.
  const handleDesignateClick = () => {
    if (!selectedPlan) return;
    setEditorError('');
    setNotice('');
    const holder = activeDefaultOf(plans, selectedPlan.room_id);
    if (holder && String(holder.id) !== String(selectedPlan.id)) {
      setDesignateConfirm(true);
      return;
    }
    runDesignate(selectedPlan);
  };

  const runDesignate = async (plan) => {
    setDesignateBusy(true);
    setEditorError('');
    try {
      await pricingApi.designatePublicDefault(selectedOrgId, plan.id);
      setDesignateConfirm(false);
      setNotice(`“${plan.name}” is now the public default for ${roomNameFor(plan.room_id) || `room ${plan.room_id}`}.`);
      await load();
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        stepUpRetryRef.current = { kind: 'designate' };
        setStepUpChallenge(challenge);
        return;
      }
      setDesignateConfirm(false);
      setEditorError(mutationErrorText(err, 'Failed to set the public default.'));
    } finally {
      setDesignateBusy(false);
    }
  };

  const handleAddOpen = () => {
    setAddForm(emptyAddForm());
    setAddError('');
    setAddOpen((prev) => !prev);
  };

  const handleAddSubmit = async () => {
    setAddError('');
    const { payload, error: validationError } = buildAddPayload(addForm);
    if (validationError) {
      setAddError(validationError);
      return;
    }
    try {
      await pricingApi.createPlan(selectedOrgId, payload);
      setAddOpen(false);
      setAddForm(emptyAddForm());
      setNotice(`Price plan “${payload.name}” created.`);
      await load();
    } catch (err) {
      // #107 defect (A) (port from !399): the create is replayed verbatim
      // on step-up completion — the typed plan is never lost to the dialog.
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        stepUpRetryRef.current = { kind: 'add' };
        setStepUpChallenge(challenge);
        return;
      }
      setAddError(mutationErrorText(err, 'Failed to create the price plan.'));
    }
  };

  const handleDelete = async () => {
    if (!selectedPlan) return;
    setEditorError('');
    setNotice('');
    setDeleteConflict(null);
    try {
      await pricingApi.deletePlan(selectedOrgId, selectedPlan.id);
      setSelectedPlanId('');
      setEditorForm(emptyEditorForm(null));
      setNotice(`Price plan “${selectedPlan.name}” deleted.`);
      await load();
    } catch (err) {
      // #107 defect (A) (port from !399): the delete is replayed verbatim on
      // step-up completion — never an error bar for a 401 STEP_UP_REQUIRED.
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        stepUpRetryRef.current = { kind: 'delete' };
        setStepUpChallenge(challenge);
        return;
      }
      const status = err?.response?.status;
      const body = err?.response?.data || {};
      if (status === 409 && body.error === 'PRICING_PLAN_IN_USE') {
        // R3: the named-references error — Core's detail[] names every
        // blocker class (live quotes, member assignments, room bindings,
        // grandfather holds). Rendered verbatim; never paraphrased away.
        setEditorError(body.message || 'This price plan is in use and cannot be deleted.');
        setDeleteConflict({
          detail: Array.isArray(body.detail) ? body.detail : [],
        });
        return;
      }
      setEditorError(mutationErrorText(err, 'Failed to delete the price plan.'));
    }
  };

  if (!canView) {
    // Fail closed: without pricing_plans_view this mount renders nothing —
    // the tab itself is already hidden by BookingsHub's tabsForPermissions.
    return null;
  }

  return (
    <div className="bkh-price-plans" data-testid="bookings-hub-tabpanel-price-plans">
      <p className="bkh-price-plans-intro">
        Every price plan in {organisationName || 'this organisation'} — room plans and org
        templates. Select a plan to edit it in its drawer: rates, Set as default, Retire. This tab is
        the only plan-management surface (the Room-edit Pricing tab is retired, M004-F0679).
      </p>

      {error ? (
        <p className="bkh-error" role="alert" data-testid="price-plans-error">{error}</p>
      ) : notice ? (
        <p className="bkh-success" role="status" data-testid="price-plans-notice">{notice}</p>
      ) : null}

      {/* A failed list read shows the ERROR instead of the table — an empty
          table next to an error would lie ("no plans" vs "could not read"). */}
      {error ? null : loading ? (
        <p className="bkh-loading" data-testid="price-plans-loading">Loading price plans…</p>
      ) : (
        <>
          {roomsWithoutDefault.length > 0 ? (
            <p className="bkh-price-plans-help bkh-price-plans-no-default" role="status" data-testid="price-plans-no-default-warning">
              No default plan set for {roomsWithoutDefault.join(', ')}. A booking that no other plan
              covers falls back to its room’s default, so a room without one has no safety net: open
              one of its plans and choose “Set as default”.
            </p>
          ) : null}
          <table className="bkh-price-plans-table" data-testid="price-plans-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Rates</th>
                <th>Scope</th>
                <th>Status</th>
                <th>Effective from</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 ? (
                <tr data-testid="price-plans-empty-row">
                  <td colSpan={6} data-testid="price-plans-empty">No price plans yet.</td>
                </tr>
              ) : plans.map((plan) => (
                <tr
                  key={plan.id}
                  data-testid="price-plans-table-row"
                  data-plan-id={plan.id}
                  onClick={() => openEditorFor(String(plan.id))}
                >
                  <td data-testid="price-plans-row-name">{plan.name}</td>
                  <td data-testid="price-plans-row-rates">{planRatesText(plan)}</td>
                  <td data-testid="price-plans-row-scope">{planScopeText(plan, roomNameFor)}</td>
                  <td data-testid="price-plans-row-status">
                    <span className="status-badge">{plan.status || 'active'}</span>
                  </td>
                  <td data-testid="price-plans-row-effective">{plan.effective_from || '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      data-testid="price-plans-row-edit"
                      onClick={(event) => { event.stopPropagation(); openEditorFor(String(plan.id)); }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="bkh-price-plans-actions">
            <button
              type="button"
              className="btn btn-primary"
              data-testid="price-plans-add"
              onClick={handleAddOpen}
              disabled={!canManage}
              title={canManage ? undefined : MANAGE_TOOLTIP}
            >
              Add price plan
            </button>
          </div>

          {addOpen && (
            <section className="bkh-price-plans-form" data-testid="price-plans-add-form">
              <h3>Add price plan</h3>
              {addError ? <p className="bkh-error" role="alert" data-testid="price-plans-add-error">{addError}</p> : null}
              <div className="bkh-price-plans-grid">
                <label>
                  Name
                  <input
                    type="text"
                    name="name"
                    data-testid="price-plans-add-name"
                    value={addForm.name}
                    onChange={handleAddChange}
                  />
                </label>
                <label>
                  Scope
                  <select
                    name="scope"
                    data-testid="price-plans-add-scope"
                    value={addForm.scope}
                    onChange={handleAddChange}
                  >
                    <option value="template">Org template</option>
                    <option value="room">Room plan</option>
                  </select>
                </label>
                {addForm.scope === 'room' && (
                  <label>
                    Room
                    <select
                      name="roomId"
                      data-testid="price-plans-add-room"
                      value={addForm.roomId}
                      onChange={handleAddChange}
                    >
                      <option value="">— Choose a room —</option>
                      {Object.entries(rooms).map(([roomId, roomName]) => (
                        <option key={roomId} value={roomId}>{roomName || `Room ${roomId}`}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  Inherit from template (optional)
                  <select
                    name="templatePlanId"
                    data-testid="price-plans-add-template"
                    value={addForm.templatePlanId}
                    onChange={handleAddChange}
                  >
                    <option value="">— None: set every rate below —</option>
                    {plans.filter((plan) => toBool(plan.is_template)).map((plan) => (
                      <option key={plan.id} value={String(plan.id)}>{plan.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  First hour rate (£)
                  <input
                    type="text"
                    name="firstHourRate"
                    data-testid="price-plans-add-first-hour"
                    value={addForm.firstHourRate}
                    onChange={handleAddChange}
                    placeholder={addForm.templatePlanId ? 'optional — inherited' : '12.50'}
                  />
                </label>
                <label>
                  Long hour rate (£)
                  <input
                    type="text"
                    name="longHourRate"
                    data-testid="price-plans-add-long-hour"
                    value={addForm.longHourRate}
                    onChange={handleAddChange}
                    placeholder={addForm.templatePlanId ? 'optional — inherited' : '10.00'}
                  />
                </label>
                <label>
                  Long rate threshold (minutes)
                  <input
                    type="number"
                    name="thresholdMinutes"
                    data-testid="price-plans-add-threshold"
                    value={addForm.thresholdMinutes}
                    onChange={handleAddChange}
                    min="1"
                  />
                </label>
                <label>
                  Effective from
                  <input
                    type="date"
                    name="effectiveFrom"
                    data-testid="price-plans-add-effective-from"
                    value={addForm.effectiveFrom}
                    onChange={handleAddChange}
                  />
                </label>
              </div>
              <p className="bkh-price-plans-help">
                A plan without a template parent must state every rate (Core chk_plan_rates_present).
              </p>
              <button
                type="button"
                className="btn btn-primary"
                data-testid="price-plans-add-submit"
                onClick={handleAddSubmit}
                disabled={!canManage}
                title={canManage ? undefined : MANAGE_TOOLTIP}
              >
                Create plan
              </button>
            </section>
          )}
        </>
      )}

      {selectedPlan && (
        <div className="bkh-drawer-backdrop" {...editorDismissHandlers}>
          <aside
            className="bkh-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={`Edit ${selectedPlan.name}`}
            data-testid="price-plans-editor-drawer"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="bkh-drawer-header">
              <div>
                <h2>
                  Edit “{selectedPlan.name}”
                  <span className="bkh-price-plans-editor-status"> {selectedPlan.status || 'active'}</span>
                </h2>
              </div>
              <button type="button" className="btn btn-outline" onClick={closeEditor}>Close</button>
            </div>

            {editorError ? <p className="bkh-error" role="alert" data-testid="price-plans-editor-error">{editorError}</p> : null}
            {deleteConflict ? (
              <div data-testid="price-plans-delete-conflict">
                <p className="bkh-price-plans-help">
                  {deleteConflict.detail.length > 0
                    ? 'Still referenced by:'
                    : 'The backend refused the delete because this plan is still in use.'}
                </p>
                <ul data-testid="price-plans-delete-refs">
                  {deleteConflict.detail.map((entry, index) => (
                    <li key={`${entry.reason}-${index}`} data-testid="price-plans-delete-ref">
                      {entry.reason}: {entry.ref}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="bkh-price-plans-grid">
              <label>
                Name
                <input
                  type="text"
                  name="name"
                  data-testid="price-plans-name-input"
                  value={editorForm.name}
                  onChange={handleEditorChange}
                />
              </label>
              <label>
                First hour rate (£)
                <input
                  type="text"
                  name="firstHourRate"
                  data-testid="price-plans-first-hour-rate"
                  value={editorForm.firstHourRate}
                  onChange={handleEditorChange}
                  placeholder={poundsFromMinor(selectedPlan.first_hour_rate_minor) || 'not set'}
                />
              </label>
              <label>
                Long hour rate (£)
                <input
                  type="text"
                  name="longHourRate"
                  data-testid="price-plans-long-hour-rate"
                  value={editorForm.longHourRate}
                  onChange={handleEditorChange}
                  placeholder={poundsFromMinor(selectedPlan.long_hour_rate_minor) || 'not set'}
                />
              </label>
              <label>
                Long rate threshold (minutes)
                <input
                  type="number"
                  name="thresholdMinutes"
                  data-testid="price-plans-threshold"
                  value={editorForm.thresholdMinutes}
                  onChange={handleEditorChange}
                  min="1"
                  placeholder={selectedPlan.long_rate_threshold_minutes != null
                    ? String(selectedPlan.long_rate_threshold_minutes)
                    : 'not set'}
                />
              </label>
            </div>
            <p className="bkh-price-plans-help">
              Leave a rate blank to keep the current rate. Current rates:{' '}
              {planRatesText(selectedPlan)}.
            </p>

            {/* M004-F0649 (design §3.7): zones, behind REACT_APP_PLAN_ZONES_ENABLED
                (default OFF) until Core M005-F0085 (window authoring) is on
                production. Edits the SELECTED plan's zones only. */}
            {isPlanZonesBackendAvailable() ? (
              <PlanZonesSection
                organisationId={selectedOrgId}
                plan={selectedPlan}
                canManage={canManage}
                onSaved={async (message) => { await load(); setNotice(message); }}
              />
            ) : null}

            {/* M004-F0679: the two actions only the retired room Pricing tab
                offered. Both are inline (no modal); Retire rides the ONE Save. */}
            <div className="bkh-price-plans-plan-actions" data-testid="price-plans-plan-actions">
              {!toBool(selectedPlan.is_template) && toBool(selectedPlan.is_public_default)
                && (selectedPlan.status || 'active') === 'active' ? (
                  <p className="bkh-price-plans-help" data-testid="price-plans-is-default">
                    This is the public default for {roomNameFor(selectedPlan.room_id) || `room ${selectedPlan.room_id}`}: the plan
                    a booking falls back to when no other plan applies to it.
                  </p>
                ) : null}
              {canDesignateDefault(selectedPlan) && !designateConfirm ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  data-testid="price-plans-set-default"
                  onClick={handleDesignateClick}
                  disabled={!canManage || designateBusy}
                  title={canManage ? undefined : MANAGE_TOOLTIP}
                >
                  Set as default
                </button>
              ) : null}
              {designateConfirm ? (
                <div data-testid="price-plans-designate-confirm">
                  <p className="bkh-price-plans-help" data-testid="price-plans-designate-confirm-text">
                    This will stop “{activeDefaultOf(plans, selectedPlan.room_id)?.name}” from being{' '}
                    {roomNameFor(selectedPlan.room_id) || `room ${selectedPlan.room_id}`}’s fallback rate:
                    “{selectedPlan.name}” will apply instead to anyone with no other plan. No booking is repriced.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    data-testid="price-plans-designate-submit"
                    onClick={() => runDesignate(selectedPlan)}
                    disabled={designateBusy}
                  >
                    {designateBusy ? 'Saving…' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    data-testid="price-plans-designate-cancel"
                    onClick={() => setDesignateConfirm(false)}
                    disabled={designateBusy}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
              {(selectedPlan.status || 'active') !== 'retired' ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  data-testid="price-plans-retire"
                  onClick={() => setRetireRequested((requested) => !requested)}
                  disabled={!canManage}
                  title={canManage ? undefined : MANAGE_TOOLTIP}
                >
                  {retireRequested ? 'Keep this plan' : 'Retire this plan'}
                </button>
              ) : null}
              {retireRequested ? (
                <p className="bkh-price-plans-help" data-testid="price-plans-retire-note">
                  “{selectedPlan.name}” will be retired when you Save: it stops being offered for new
                  bookings. The impact is shown below first.
                </p>
              ) : null}
            </div>

            {/* R4 INLINE: the reprice impact for the current form — shown
                automatically while a rate edit is in play, re-queried by the
                grandfather toggle, and BLOCKING Save until readable. Not a
                modal: a rate change is not destructive (M004-F0637 §2). */}
            {patchTouchesRatesNow && (
              <div data-testid="price-plans-preview">
                {impactLoading ? (
                  <p className="bkh-loading" data-testid="price-plans-preview-loading">Checking affected bookings…</p>
                ) : impactError ? (
                  <p className="bkh-error" role="alert" data-testid="price-plans-preview-error">
                    {impactError} This save is blocked: a rate change must never apply without its
                    impact shown.
                  </p>
                ) : impact ? (
                  <ul className="bkh-price-plans-impact" data-testid="price-plans-preview-impact">
                    <li>
                      <span data-testid="price-plans-preview-to-reprice">{impact.toReprice}</span>{' '}
                      upcoming booking(s) will be repriced by this change.
                    </li>
                    <li>
                      <span data-testid="price-plans-preview-held">{impact.held}</span>{' '}
                      booking(s) already billed or under a grandfather hold will keep their current
                      rate (M005-F0050 — “Rate held (grandfathered)”).
                    </li>
                    <li>
                      <span data-testid="price-plans-preview-refused">{impact.refusedBilled}</span>{' '}
                      already-billed booking(s) will be refused a reprice.
                    </li>
                  </ul>
                ) : null}
                <label className="bkh-price-plans-grandfather">
                  <input
                    type="checkbox"
                    data-testid="price-plans-grandfather-toggle"
                    checked={grandfather}
                    disabled={!canManage}
                    onChange={(event) => setGrandfather(event.target.checked)}
                  />
                  {' '}Grandfather existing bookings — bookings already on this plan keep their current
                  rate until each ends; new bookings always get the new rate.
                </label>
              </div>
            )}

            <div className="bkh-price-plans-editor-actions">
              <button
                type="button"
                className="btn btn-primary"
                data-testid="price-plans-save"
                onClick={handleSaveClick}
                disabled={!canManage || (patchTouchesRatesNow && (impactLoading || !impact || Boolean(impactError)))}
                title={!canManage ? MANAGE_TOOLTIP : undefined}
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-danger"
                data-testid="price-plans-delete"
                onClick={handleDelete}
                disabled={!canManage}
                title={canManage ? undefined : MANAGE_TOOLTIP}
              >
                Delete
              </button>
            </div>
            <p className="bkh-price-plans-help">
              A rate edit saves in ONE click once its impact is shown. Deleting a plan that any
              live booking quote, member assignment, room binding or grandfather hold still
              references is refused by the backend — nothing is deleted silently.
            </p>
          </aside>
        </div>
      )}

      <StepUpDialog
        challenge={stepUpChallenge}
        title="Verify pricing changes"
        description="Confirm your identity before StudioAEye changes this organisation's price plans."
        onComplete={completeStepUp}
        onCancel={() => { setStepUpChallenge(null); stepUpRetryRef.current = null; }}
      />
    </div>
  );
};

export default PricePlansHubTab;
